import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../config';
import '../styles/Account.css';

const Account = () => {
  const [subscription, setSubscription] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [message, setMessage] = useState('');
  const { token, loading } = useAuth();
  const navigate = useNavigate();

  const location = useLocation();

  const fetchSubscription = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/subscription`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data.status === 'success') {
        setSubscription(data.subscription);
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
    } finally {
      setPageLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!loading && !token) {
      navigate('/login');
      return;
    }
    if (token) {
      fetchSubscription();
    }
    if (location.state?.message) {
      setMessage(location.state.message);
      // Clear state so message doesn't persist on refresh unique to this navigation
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [token, loading, navigate, fetchSubscription, location.state, location.pathname]);

  const handleReactivate = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/subscription/reactivate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ subscription_id: subscription.id }),
      });

      const data = await response.json();
      if (data.status === 'success') {
        setMessage('Prenumerationen har återaktiverats.');
        fetchSubscription();
      } else {
        setMessage('Kunde inte återaktivera prenumerationen: ' + data.error);
      }
    } catch (error) {
      setMessage('Ett fel uppstod: ' + error.message);
    }
  };

  const handleCancel = async () => {
    if (
      !window.confirm(
        'Är du säker på att du vill avsluta din prenumeration? Den kommer att vara aktiv till periodens slut.'
      )
    ) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/subscription/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ subscription_id: subscription.id }),
      });

      const data = await response.json();
      if (data.status === 'success') {
        setMessage('Prenumerationen har avslutats och löper ut vid periodens slut.');
        fetchSubscription(); // Refresh state
      } else {
        setMessage('Kunde inte avsluta prenumerationen: ' + data.error);
      }
    } catch (error) {
      setMessage('Ett fel uppstod: ' + error.message);
    }
  };

  const handleUpdatePlan = async (newPlan) => {
    const planNames = {
      basic: 'Bevakning (99 kr/mån)',
      premium: 'Auto-Bokning (199 kr/mån)',
      pro: 'Auto-Bokning Pro (300 kr/mån)',
    };
    const targetName = planNames[newPlan] || newPlan;
    const planRanks = { basic: 1, premium: 2, pro: 3 };
    const currentRank =
      subscription?.plan_name === 'Auto-Bokning Pro'
        ? 3
        : subscription?.plan_name === 'Auto-Bokning'
          ? 2
          : 1;
    const newRank = planRanks[newPlan] || 1;

    let confirmMessage;
    if (newRank < currentRank) {
      confirmMessage = `Är du säker på att du vill byta till ${targetName}? Ändringen träder i kraft från och med nästa betalningsperiod och du behåller dina nuvarande förmåner fram till dess.`;
    } else {
      confirmMessage = `Är du säker på att du vill byta till ${targetName}? Du debiteras omedelbart för uppgraderingen och tillgodoräknas för din återstående prenumerationstid.`;
    }

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/subscription/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          subscription_id: subscription.id,
          plan: newPlan,
        }),
      });

      const data = await response.json();
      if (data.status === 'success') {
        setMessage('Din prenumeration har uppdaterats.');
        fetchSubscription();
      } else {
        setMessage('Kunde inte uppdatera prenumerationen: ' + data.error);
      }
    } catch (error) {
      setMessage('Ett fel uppstod: ' + error.message);
    }
  };

  if (loading || pageLoading) {
    return (
      <div className="account-wrapper">
        <div className="account-container">Laddar...</div>
      </div>
    );
  }

  return (
    <div className="account-wrapper">
      <div className="account-container">
        <div className="account-header">
          <h1 className="account-title">Mitt Konto</h1>
          <p className="account-subtitle">Hantera din prenumeration och kontouppgifter</p>
        </div>

        {message && (
          <div
            style={{
              padding: '10px',
              background: '#e3f2fd',
              borderRadius: '8px',
              marginBottom: '20px',
              color: '#0d47a1',
            }}
          >
            {message}
          </div>
        )}

        <div className="subscription-card">
          <div className="card-header">
            <h2 style={{ fontSize: '20px', margin: 0 }}>Nuvarande Prenumeration</h2>
          </div>
          {subscription ? (
            <>
              {subscription.scheduled_change ? (
                <div
                  style={{
                    marginBottom: '30px',
                    background: '#fff',
                    borderRadius: '12px',
                    border: '1px solid #eee',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(100px, auto) 1fr 1fr 1fr',
                      gap: '15px',
                      alignItems: 'center',
                      padding: '20px',
                      borderBottom: '1px solid #eee',
                      backgroundColor: '#f9f9f9',
                    }}
                  >
                    <div style={{ fontWeight: '600', color: '#666', fontSize: '0.9rem' }}>
                      Nuvarande:
                    </div>
                    <div style={{ fontWeight: '700', fontSize: '1rem' }}>
                      {subscription.plan_name}
                    </div>
                    <div style={{ fontSize: '1rem' }}>
                      {subscription.amount / 100} {subscription.currency.toUpperCase()}/mån
                    </div>
                    <div>
                      <span
                        style={{
                          display: 'block',
                          fontSize: '0.75rem',
                          textTransform: 'uppercase',
                          color: '#888',
                          fontWeight: '600',
                          marginBottom: '4px',
                        }}
                      >
                        Betalades
                      </span>
                      <span style={{ fontWeight: '500' }}>
                        {new Date(subscription.current_period_start * 1000).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(100px, auto) 1fr 1fr 1fr',
                      gap: '15px',
                      alignItems: 'center',
                      padding: '20px',
                      backgroundColor: '#fff',
                    }}
                  >
                    <div style={{ fontWeight: '600', color: '#666', fontSize: '0.9rem' }}>
                      Nästa period:
                    </div>
                    <div style={{ fontWeight: '700', fontSize: '1rem' }}>
                      {subscription.scheduled_change.plan_name}
                    </div>
                    <div style={{ fontSize: '1rem' }}>
                      {subscription.scheduled_change.amount / 100}{' '}
                      {subscription.scheduled_change.currency.toUpperCase()}/mån
                    </div>
                    <div>
                      <span
                        style={{
                          display: 'block',
                          fontSize: '0.75rem',
                          textTransform: 'uppercase',
                          color: '#888',
                          fontWeight: '600',
                          marginBottom: '4px',
                        }}
                      >
                        Nästa betalning
                      </span>
                      <span style={{ fontWeight: '500' }}>
                        {new Date(
                          subscription.scheduled_change.effective_date * 1000
                        ).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="subscription-details">
                  <div className="detail-item">
                    <label>Plan</label>
                    <div className="value">{subscription.plan_name}</div>
                  </div>
                  <div className="detail-item">
                    <label>Pris</label>
                    <div className="value">
                      {subscription.amount / 100} {subscription.currency.toUpperCase()}/mån
                    </div>
                  </div>
                  <div className="detail-item">
                    <label>
                      {subscription.cancel_at_period_end ? 'Slutar gälla' : 'Nästa debitering'}
                    </label>
                    {/* Multiply by 1000 because Stripe returns seconds, JS expects milliseconds */}
                    <div className="value">
                      {new Date(subscription.current_period_end * 1000).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              )}

              <div className="account-actions">
                {subscription.cancel_at_period_end ? (
                  <button className="action-button btn-primary" onClick={handleReactivate}>
                    Återaktivera prenumeration
                  </button>
                ) : (
                  <>
                    {subscription.plan_name === 'Bevakning' && (
                      <button
                        className="action-button btn-primary"
                        onClick={() => navigate('/plans')}
                        style={{ marginRight: '10px', marginBottom: '8px' }}
                      >
                        Uppgradera plan
                      </button>
                    )}
                    {subscription.plan_name === 'Auto-Bokning' && (
                      <>
                        <button
                          className="action-button btn-primary"
                          onClick={() => handleUpdatePlan('pro')}
                          style={{
                            marginRight: '10px',
                            marginBottom: '8px',
                            background: '#111827',
                          }}
                        >
                          Uppgradera till Auto-Bokning Pro (Obegränsad)
                        </button>
                        <button
                          className="action-button"
                          onClick={() =>
                            !subscription.scheduled_change && handleUpdatePlan('basic')
                          }
                          disabled={!!subscription.scheduled_change}
                          style={{
                            marginRight: '10px',
                            marginBottom: '8px',
                            backgroundColor: '#e0e0e0',
                            color: '#333',
                            opacity: subscription.scheduled_change ? 0.6 : 1,
                            cursor: subscription.scheduled_change ? 'not-allowed' : 'pointer',
                          }}
                        >
                          Byt till Bevakning
                        </button>
                      </>
                    )}
                    {subscription.plan_name === 'Auto-Bokning Pro' && (
                      <>
                        <button
                          className="action-button"
                          onClick={() =>
                            !subscription.scheduled_change && handleUpdatePlan('premium')
                          }
                          disabled={!!subscription.scheduled_change}
                          style={{
                            marginRight: '10px',
                            marginBottom: '8px',
                            backgroundColor: '#e0e0e0',
                            color: '#333',
                            opacity: subscription.scheduled_change ? 0.6 : 1,
                            cursor: subscription.scheduled_change ? 'not-allowed' : 'pointer',
                          }}
                        >
                          Byt till Auto-Bokning (300h)
                        </button>
                        <button
                          className="action-button"
                          onClick={() =>
                            !subscription.scheduled_change && handleUpdatePlan('basic')
                          }
                          disabled={!!subscription.scheduled_change}
                          style={{
                            marginRight: '10px',
                            marginBottom: '8px',
                            backgroundColor: '#e0e0e0',
                            color: '#333',
                            opacity: subscription.scheduled_change ? 0.6 : 1,
                            cursor: subscription.scheduled_change ? 'not-allowed' : 'pointer',
                          }}
                        >
                          Byt till Bevakning
                        </button>
                      </>
                    )}
                    <button className="action-button btn-danger" onClick={handleCancel}>
                      Avsluta prenumeration
                    </button>
                  </>
                )}
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <p style={{ marginBottom: '20px', color: '#666' }}>
                Du har ingen aktiv prenumeration just nu.
              </p>
              <button className="action-button btn-primary" onClick={() => navigate('/plans')}>
                Se våra planer
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Account;
